// ManiDine Restaurant Web — src/pages/reports/FinancialReports.jsx — Updated: 2026-04-01 16:22 (America/Edmonton)
import React, { useEffect, useMemo, useState } from "react";
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../../firebase";
import useRestaurantContext from "../../hooks/useRestaurantContext";
import {
  ReportCard,
  ReportFilterBar,
  ReportTable,
} from "../../components/reports";

const defaultDateRange = { preset: "7d", start: "", end: "" };

const RESTAURANT_SERVICE_FEE_PER_ORDER = 1.99;
const RESTAURANT_MEMBERSHIP_FEE_RATE = 0.1;
const RESTAURANT_MEMBERSHIP_FEE_CAP = 200;

const parseNumber = (v, fallback = 0) => {
  if (typeof v === "number") return v;
  if (typeof v === "string") {
    const n = parseFloat(v);
    return Number.isNaN(n) ? fallback : n;
  }
  return fallback;
};

const roundTo2 = (value) => Number(parseNumber(value).toFixed(2));

const formatCurrency = (value) =>
  new Intl.NumberFormat("en-CA", {
    style: "currency",
    currency: "CAD",
  }).format(parseNumber(value));

const toDateSafe = (value) => {
  if (!value) return null;
  if (typeof value?.toDate === "function") return value.toDate();
  if (value instanceof Date) return value;
  if (typeof value?.seconds === "number") return new Date(value.seconds * 1000);
  if (typeof value?._seconds === "number") return new Date(value._seconds * 1000);
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

const fmtDate = (value) => {
  const d = toDateSafe(value);
  if (!d) return "—";
  return d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const resolveDateRange = (dateRange) => {
  const now = new Date();
  let start = null;
  let end = null;

  if (dateRange.preset === "today") {
    start = new Date();
    start.setHours(0, 0, 0, 0);
    end = new Date(start);
    end.setDate(end.getDate() + 1);
  } else if (dateRange.preset === "7d") {
    end = new Date(now);
    end.setHours(23, 59, 59, 999);
    start = new Date(end);
    start.setDate(start.getDate() - 6);
    start.setHours(0, 0, 0, 0);
  } else if (dateRange.preset === "30d") {
    end = new Date(now);
    end.setHours(23, 59, 59, 999);
    start = new Date(end);
    start.setDate(start.getDate() - 29);
    start.setHours(0, 0, 0, 0);
  } else if (dateRange.preset === "custom") {
    if (dateRange.start) {
      start = new Date(dateRange.start);
      start.setHours(0, 0, 0, 0);
    }
    if (dateRange.end) {
      end = new Date(dateRange.end);
      end.setHours(23, 59, 59, 999);
    }
  }

  return { start, end };
};

const normalizeStatus = (status) => String(status || "").toLowerCase().trim();

const isRestaurantPayoutEligibleStatus = (status) => {
  const s = normalizeStatus(status);
  if (!s) return false;

  if (
    s.includes("cancel") ||
    s.includes("reject") ||
    s.includes("refun") ||
    s.includes("void") ||
    s.includes("declin") ||
    s.includes("fail")
  ) {
    return false;
  }

  return s.includes("complete") || s.includes("deliver");
};

const getRestaurantSettlementDate = (order) =>
  toDateSafe(
    order?.deliveredAt ||
      order?.completedAt ||
      order?.pickedUpAt ||
      order?.updatedAt ||
      order?.createdAt ||
      order?.placedAt ||
      order?.orderTime
  );

const firstDefinedMoneyValue = (values) => {
  for (const value of values) {
    if (value === null || value === undefined || value === "") continue;
    const n = Number(value);
    if (Number.isFinite(n)) return n;
  }
  return null;
};

const getPaymentStatus = (order) =>
  String(order?.payment?.status || order?.paymentStatus || "").toLowerCase();

const extractRestaurantOrderStripeFee = (order) => {
  const explicit = firstDefinedMoneyValue([
    order?.stripeProcessingFee,
    order?.restaurantProcessingFee,
    order?.payment?.stripeProcessingFee,
    order?.payment?.restaurantProcessingFee,
    order?.payment?.processingFee,
    order?.payment?.processorFee,
    order?.payment?.stripeFee,
    order?.payment?.stripeFeeAmount,
    order?.payment?.feeAmount,
    order?.payment?.fee,
    order?.payment?.processing_fee,
    order?.payout?.restaurantProcessingFee,
    order?.payout?.stripeProcessingFee,
  ]);

  return roundTo2(Math.max(0, explicit ?? 0));
};

const extractRestaurantOrderServiceFee = (order) => {
  const explicit = firstDefinedMoneyValue([
    order?.restaurantServiceFee,
    order?.payout?.restaurantServiceFee,
  ]);

  return roundTo2(Math.max(0, explicit ?? RESTAURANT_SERVICE_FEE_PER_ORDER));
};

const getRestaurantOrderTax = (order) =>
  roundTo2(parseNumber(order?.taxAmount ?? order?.tax));

const getRestaurantOrderFoodSubtotal = (order) => {
  const explicitSubtotal = firstDefinedMoneyValue([
    order?.itemSubtotal,
    order?.subtotal,
    order?.itemsSubtotal,
    order?.foodSubtotal,
  ]);

  if (explicitSubtotal != null) {
    return roundTo2(Math.max(0, explicitSubtotal));
  }

  const total = firstDefinedMoneyValue([
    order?.totalAmount,
    order?.total,
  ]);

  if (total != null) {
    const taxAmount = getRestaurantOrderTax(order);
    const deliveryFee = roundTo2(parseNumber(order?.deliveryFee));
    const tipAmount = roundTo2(parseNumber(order?.tipAmount ?? order?.tip));
    return roundTo2(Math.max(0, total - taxAmount - deliveryFee - tipAmount));
  }

  return 0;
};

const computeRestaurantOrderFinancials = (order) => {
  const itemSubtotal = getRestaurantOrderFoodSubtotal(order);
  const taxAmount = getRestaurantOrderTax(order);

  const explicitGross = firstDefinedMoneyValue([
    order?.restaurantPayoutGross,
    order?.restaurantGross,
    order?.payout?.restaurantGross,
    order?.payout?.restaurantPayoutGross,
  ]);

  const computedGross = roundTo2(Math.max(0, itemSubtotal + taxAmount));
  const gross = roundTo2(Math.max(0, explicitGross ?? computedGross));
  const serviceFeeApplied = extractRestaurantOrderServiceFee(order);
  const stripeFeeApplied = extractRestaurantOrderStripeFee(order);
  const totalDeductionsBeforeMembership = roundTo2(serviceFeeApplied + stripeFeeApplied);
  const netBeforeMembership = roundTo2(
    Math.max(0, gross - totalDeductionsBeforeMembership)
  );

  return {
    itemSubtotal,
    taxAmount,
    gross,
    serviceFeeApplied,
    stripeFeeApplied,
    totalDeductionsBeforeMembership,
    netBeforeMembership,
  };
};

const getWeekStart = (value) => {
  const d = new Date(value);
  d.setHours(0, 0, 0, 0);
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  return d;
};

const getWeekEnd = (weekStart) => {
  const d = new Date(weekStart);
  d.setDate(d.getDate() + 6);
  d.setHours(23, 59, 59, 999);
  return d;
};

const getWeekKey = (weekStart) => {
  const y = weekStart.getFullYear();
  const m = String(weekStart.getMonth() + 1).padStart(2, "0");
  const d = String(weekStart.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const getMonthKeyForPeriod = (periodStart, periodEnd) => {
  const anchor =
    periodEnd.getTime() > periodStart.getTime()
      ? new Date(periodEnd.getTime() - 1)
      : periodStart;
  const y = anchor.getFullYear();
  const m = String(anchor.getMonth() + 1).padStart(2, "0");
  return `${y}-${m}`;
};

const formatPeriodLabel = (periodStart, periodEnd) => {
  if (!periodStart || !periodEnd) return "—";

  const sameYear = periodStart.getFullYear() === periodEnd.getFullYear();
  if (sameYear) {
    const startTxt = periodStart.toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
    });
    const endTxt = periodEnd.toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
    });
    return `${startTxt} – ${endTxt}, ${periodStart.getFullYear()}`;
  }

  const startTxt = periodStart.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
  const endTxt = periodEnd.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
  return `${startTxt} – ${endTxt}`;
};

const summarizeWeeks = (weeks) =>
  (weeks || []).reduce(
    (acc, week) => {
      acc.count += 1;
      acc.orders += parseNumber(week?.orderCount);
      acc.restaurantSales += parseNumber(week?.restaurantSales);
      acc.serviceFees += parseNumber(week?.serviceFeeTotal);
      acc.stripeFees += parseNumber(week?.stripeFeeTotal);
      acc.membershipFees += parseNumber(week?.membershipFeeCharged);
      acc.totalDeductions += parseNumber(week?.totalDeductions);
      acc.netPayout += parseNumber(week?.netPayout);
      return acc;
    },
    {
      count: 0,
      orders: 0,
      restaurantSales: 0,
      serviceFees: 0,
      stripeFees: 0,
      membershipFees: 0,
      totalDeductions: 0,
      netPayout: 0,
    }
  );

const FinancialReports = () => {
  const { restaurantId, loading: restaurantLoading } =
    useRestaurantContext() || {};

  const [dateRange, setDateRange] = useState(defaultDateRange);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [orders, setOrders] = useState([]);

  useEffect(() => {
    const loadOrders = async () => {
      setLoading(true);
      setError("");

      try {
        if (!restaurantId) {
          setOrders([]);
          setLoading(false);
          return;
        }

        const ordersRef = collection(db, "orders");
        const ordersQuery = query(
          ordersRef,
          where("restaurantId", "==", restaurantId)
        );
        const snap = await getDocs(ordersQuery);
        const rows = snap.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }));
        setOrders(rows);
      } catch (err) {
        console.error("[FinancialReports] Load orders failed:", err);
        setError(err?.message || "Could not load restaurant payout ledger.");
        setOrders([]);
      } finally {
        setLoading(false);
      }
    };

    if (restaurantLoading) {
      setLoading(true);
      return;
    }

    loadOrders();
  }, [restaurantId, restaurantLoading]);

  const eligibleOrders = useMemo(() => {
    return orders
      .map((order) => {
        const settledAt = getRestaurantSettlementDate(order);
        const paymentStatus = getPaymentStatus(order);
        const financials = computeRestaurantOrderFinancials(order);

        return {
          ...order,
          __settledAt: settledAt,
          __paymentStatus: paymentStatus,
          __itemSubtotal: financials.itemSubtotal,
          __taxAmount: financials.taxAmount,
          __restaurantSales: financials.gross,
          __serviceFeeApplied: financials.serviceFeeApplied,
          __stripeFeeApplied: financials.stripeFeeApplied,
          __totalDeductionsBeforeMembership:
            financials.totalDeductionsBeforeMembership,
          __netBeforeMembership: financials.netBeforeMembership,
        };
      })
      .filter((order) => {
        if (!isRestaurantPayoutEligibleStatus(order?.status)) return false;
        if (!order.__settledAt) return false;
        if (
          order.__paymentStatus.includes("refund") ||
          order.__paymentStatus.includes("fail")
        ) {
          return false;
        }
        return order.__netBeforeMembership > 0;
      });
  }, [orders]);

  const weeklyRowsAll = useMemo(() => {
    if (!eligibleOrders.length) return [];

    const grouped = new Map();

    eligibleOrders.forEach((order) => {
      const weekStart = getWeekStart(order.__settledAt);
      const weekEnd = getWeekEnd(weekStart);
      const weekKey = getWeekKey(weekStart);
      const existing = grouped.get(weekKey) || {
        id: weekKey,
        weekKey,
        periodStart: weekStart,
        periodEnd: weekEnd,
        periodLabel: formatPeriodLabel(weekStart, weekEnd),
        orderCount: 0,
        itemSubtotal: 0,
        taxAmount: 0,
        restaurantSales: 0,
        serviceFeeTotal: 0,
        stripeFeeTotal: 0,
        totalDeductionsBeforeMembership: 0,
        netBeforeMembership: 0,
      };

      grouped.set(weekKey, {
        ...existing,
        orderCount: existing.orderCount + 1,
        itemSubtotal: roundTo2(existing.itemSubtotal + order.__itemSubtotal),
        taxAmount: roundTo2(existing.taxAmount + order.__taxAmount),
        restaurantSales: roundTo2(
          existing.restaurantSales + order.__restaurantSales
        ),
        serviceFeeTotal: roundTo2(
          existing.serviceFeeTotal + order.__serviceFeeApplied
        ),
        stripeFeeTotal: roundTo2(
          existing.stripeFeeTotal + order.__stripeFeeApplied
        ),
        totalDeductionsBeforeMembership: roundTo2(
          existing.totalDeductionsBeforeMembership +
            order.__totalDeductionsBeforeMembership
        ),
        netBeforeMembership: roundTo2(
          existing.netBeforeMembership + order.__netBeforeMembership
        ),
      });
    });

    const ascending = [...grouped.values()].sort(
      (a, b) => a.periodStart.getTime() - b.periodStart.getTime()
    );

    const monthState = new Map();

    const withMembership = ascending.map((row) => {
      const monthKey = getMonthKeyForPeriod(row.periodStart, row.periodEnd);
      const state = monthState.get(monthKey) || {
        monthGrossSoFar: 0,
        monthFeeChargedSoFar: 0,
      };

      const feeCandidateThisPeriod = roundTo2(
        row.restaurantSales * RESTAURANT_MEMBERSHIP_FEE_RATE
      );
      const remainingCap = roundTo2(
        Math.max(
          0,
          RESTAURANT_MEMBERSHIP_FEE_CAP - state.monthFeeChargedSoFar
        )
      );
      const membershipFeeCharged = roundTo2(
        Math.max(0, Math.min(feeCandidateThisPeriod, remainingCap))
      );
      const totalDeductions = roundTo2(
        row.totalDeductionsBeforeMembership + membershipFeeCharged
      );
      const netPayout = roundTo2(
        Math.max(0, row.netBeforeMembership - membershipFeeCharged)
      );
      const monthGrossAfter = roundTo2(
        state.monthGrossSoFar + row.restaurantSales
      );
      const monthFeeChargedAfter = roundTo2(
        Math.min(
          RESTAURANT_MEMBERSHIP_FEE_CAP,
          state.monthFeeChargedSoFar + membershipFeeCharged
        )
      );

      monthState.set(monthKey, {
        monthGrossSoFar: monthGrossAfter,
        monthFeeChargedSoFar: monthFeeChargedAfter,
      });

      return {
        ...row,
        monthKey,
        membershipFeeCharged,
        totalDeductions,
        netPayout,
        rowType: "History",
      };
    });

    return withMembership.sort(
      (a, b) => b.periodStart.getTime() - a.periodStart.getTime()
    );
  }, [eligibleOrders]);

  const filteredWeeks = useMemo(() => {
    if (!weeklyRowsAll.length) return [];
    const { start, end } = resolveDateRange(dateRange);

    return weeklyRowsAll.filter((row) => {
      const periodStart = row.periodStart;
      const periodEnd = row.periodEnd;
      if (!periodStart || !periodEnd) return false;

      // Include a pay period when it overlaps the selected range.
      // Example: "Today" inside Mar 30 – Apr 5 should still include that week row.
      if (start && periodEnd < start) return false;
      if (end && periodStart > end) return false;
      return true;
    });
  }, [weeklyRowsAll, dateRange]);

  const summary = useMemo(() => summarizeWeeks(filteredWeeks), [filteredWeeks]);

  const currentWeekKey = useMemo(() => getWeekKey(getWeekStart(new Date())), []);
  const lastWeekKey = useMemo(() => {
    const d = getWeekStart(new Date());
    d.setDate(d.getDate() - 7);
    return getWeekKey(d);
  }, []);

  const currentWeekRow = useMemo(
    () => weeklyRowsAll.find((row) => row.weekKey === currentWeekKey) || null,
    [weeklyRowsAll, currentWeekKey]
  );

  const lastWeekRow = useMemo(
    () => weeklyRowsAll.find((row) => row.weekKey === lastWeekKey) || null,
    [weeklyRowsAll, lastWeekKey]
  );

  const ledgerRows = useMemo(
    () =>
      filteredWeeks.map((row) => ({
        ...row,
        id: row.weekKey,
        rowType:
          row.weekKey === currentWeekKey
            ? "Current week"
            : row.weekKey === lastWeekKey
            ? "Last week"
            : "Earlier week",
      })),
    [filteredWeeks, currentWeekKey, lastWeekKey]
  );

  if (restaurantLoading) {
    return (
      <div className="reports-section">
        <p className="muted">Loading restaurant…</p>
      </div>
    );
  }

  if (!restaurantId) {
    return (
      <div className="reports-section">
        <p className="muted">No restaurant found. Please sign in again.</p>
      </div>
    );
  }

  return (
    <div className="reports-section">
      <ReportFilterBar
        dateRange={dateRange}
        onDateRangeChange={setDateRange}
      />

      {error ? (
        <div
          style={{
            padding: 14,
            borderRadius: 14,
            background: "rgba(239, 68, 68, 0.08)",
            border: "1px solid rgba(239, 68, 68, 0.18)",
            color: "#991b1b",
            marginBottom: 16,
          }}
        >
          {error}
        </div>
      ) : null}

      <section
        style={{
          marginBottom: 16,
          padding: 14,
          borderRadius: 14,
          background: "rgba(15, 23, 42, 0.04)",
          color: "#475569",
          lineHeight: 1.6,
        }}
      >
        This page now shows a <strong>live payout ledger from actual order details</strong>,
        grouped by pay period dates. <strong>Restaurant sales here mean food subtotal + tax only.</strong>
        Delivery fees and customer tips are excluded. The <strong>Payments / Payout Ledger</strong> page
        remains the place for admin-paid payout batches and Stripe transfer history.
      </section>

      <section className="reports-kpi-grid">
        <ReportCard
          title="Current Week Net Payout"
          value={formatCurrency(currentWeekRow?.netPayout || 0)}
          subtitle={currentWeekRow?.periodLabel || "No eligible orders this week"}
        />
        <ReportCard
          title="Last Week Net Payout"
          value={formatCurrency(lastWeekRow?.netPayout || 0)}
          subtitle={lastWeekRow?.periodLabel || "No eligible orders last week"}
        />
        <ReportCard
          title="Pay Periods In Range"
          value={summary.count}
          subtitle={`${summary.orders} eligible orders`}
        />
        <ReportCard
          title="Restaurant Sales"
          value={formatCurrency(summary.restaurantSales)}
          subtitle="Food subtotal + tax only"
        />
      </section>

      <section className="reports-kpi-grid reports-kpi-grid--small">
        <ReportCard
          title="ManiDine Service Fees"
          value={formatCurrency(summary.serviceFees)}
          subtitle="$1.99 per eligible order"
        />
        <ReportCard
          title="Stripe Fees"
          value={formatCurrency(summary.stripeFees)}
          subtitle="Actual Stripe fees on included orders"
        />
        <ReportCard
          title="Membership Estimate"
          value={formatCurrency(summary.membershipFees)}
          subtitle="10% monthly, capped at $200"
        />
        <ReportCard
          title="Net Payout"
          value={formatCurrency(summary.netPayout)}
          subtitle="Live estimate from order details"
        />
      </section>

      <section className="reports-row">
        <div className="reports-column">
          <h3 className="reports-section-title">Weekly payout ledger</h3>
          <ReportTable
            columns={[
              { title: "Pay Period", dataIndex: "periodLabel", key: "periodLabel" },
              { title: "Type", dataIndex: "rowType", key: "rowType" },
              { title: "Orders", dataIndex: "orderCount", key: "orderCount" },
              {
                title: "Restaurant Sales",
                dataIndex: "restaurantSales",
                key: "restaurantSales",
                render: (v) => formatCurrency(v),
              },
              {
                title: "Service Fees",
                dataIndex: "serviceFeeTotal",
                key: "serviceFeeTotal",
                render: (v) => formatCurrency(v),
              },
              {
                title: "Stripe Fees",
                dataIndex: "stripeFeeTotal",
                key: "stripeFeeTotal",
                render: (v) => formatCurrency(v),
              },
              {
                title: "Membership",
                dataIndex: "membershipFeeCharged",
                key: "membershipFeeCharged",
                render: (v) => formatCurrency(v),
              },
              {
                title: "Total Deductions",
                dataIndex: "totalDeductions",
                key: "totalDeductions",
                render: (v) => formatCurrency(v),
              },
              {
                title: "Net Payout",
                dataIndex: "netPayout",
                key: "netPayout",
                render: (v) => formatCurrency(v),
              },
            ]}
            data={ledgerRows}
          />
          {!loading && ledgerRows.length === 0 && (
            <p className="reports-empty">
              No live payout ledger data is available for this period.
            </p>
          )}
        </div>
      </section>

      <div
        style={{
          border: "1px solid rgba(15, 23, 42, 0.08)",
          borderRadius: 16,
          padding: 16,
          background: "rgba(255, 250, 240, 0.65)",
          color: "#475569",
          lineHeight: 1.6,
        }}
      >
        This live ledger is calculated from eligible restaurant orders by week using the same
        restaurant-only base: <strong>food subtotal + tax</strong>. Delivery fees and customer tips are excluded.
        Service fees and Stripe fees are taken from each order when available, and the membership line is a
        live weekly estimate using the 10% monthly rule capped at $200.
      </div>

      {loading && (
        <div className="reports-loading">Loading data…</div>
      )}
    </div>
  );
};

export default FinancialReports;
